import React from "react";
import { Link } from "react-router-dom";
import SEO, { buildFaqJsonLd } from "@/components/SEO";
import ContactForm from "@/components/ContactForm";
import ProcessSteps from "@/components/ProcessSteps";
import FAQAccordion from "@/components/FAQAccordion";
import CTASection from "@/components/CTASection";
import BreadcrumbNav from "@/components/BreadcrumbNav";
import { CloudRain, Sun, Wind, Hammer, Droplets, ShieldCheck } from "lucide-react";

const BREADCRUMBS = [{ label: "Home", to: "/" }, { label: "Roofing" }];

const FAQS = [
  { q: "Do you provide roof inspections in Austin?", a: "Yes. We schedule visual roof inspections for Austin and Central Texas homeowners, document what we see, and walk through findings with you." },
  { q: "Do you guarantee insurance claim approvals?", a: "No. We document what we see honestly, install temporary protection where appropriate, and provide clear repair estimates. We do not promise insurance approvals." },
  { q: "How long does a roof replacement take?", a: "Most residential shingle roofs replace in 1–3 days. Steeper roofs, larger square footage, or metal roof coordination can extend timelines." },
  { q: "What roofing concerns are most common in Central Texas?", a: "Hail, wind, intense UV, heat, sudden storms, aging shingles, poor ventilation, water intrusion at flashing, missing shingles, drainage issues, and tree damage." },
  { q: "Can you handle emergency leaks?", a: "We handle storm-related triage and can install temporary protection where conditions allow. After-hours surge response is priced separately unless a plan specifically includes an emergency credit." },
];

export default function Roofing() {
  return (
    <>
      <SEO
        title="Roofing Contractor Austin TX | Black Ridge Construction"
        description="Honest roof inspections, repairs, and replacements for Austin and Central Texas homes. Storm follow-up, flashing, ventilation, and leak triage."
        jsonLd={buildFaqJsonLd(FAQS)}
      />

      <section className="relative bg-br-black text-white">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1503594384566-461fe158e797?w=1800&q=80" alt="Roof shingles up close" className="w-full h-full object-cover" />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="relative container-br py-12 lg:py-20 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <BreadcrumbNav dark items={BREADCRUMBS} />
            <div className="br-eyebrow text-br-bronze-2 mt-4">Austin Roofing</div>
            <h1 className="br-headline text-4xl sm:text-5xl lg:text-6xl mt-3">Roofing Built for Central Texas Weather</h1>
            <p className="mt-4 text-gray-200 max-w-2xl text-base sm:text-lg">From routine inspections to storm follow-up, full repairs, and roof replacements — Black Ridge Construction handles roofing honestly. We document what we see, provide clear estimates, and never promise insurance approvals.</p>
            <ul className="mt-5 grid sm:grid-cols-2 gap-2 text-sm text-gray-300 max-w-xl">
              {["Roof inspections","Roof repairs","Roof replacement","Storm damage support","Leak detection","Flashing & ventilation","Pipe boot replacement","Gutter-related water intrusion"].map(t => (
                <li key={t} className="flex gap-2"><span className="text-br-bronze-2">▪</span>{t}</li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-5">
            <div className="bg-white text-gray-900 rounded p-6 shadow-2xl">
              <ContactForm endpoint="/leads/roofing" title="Schedule a Roof Inspection" defaultService="Roofing" source_page="roofing" successMessage="Thanks for requesting a roof inspection. We'll follow up to schedule a time on your property." />
            </div>
          </div>
        </div>
      </section>

      <section className="section-pad bg-br-paper">
        <div className="container-br grid lg:grid-cols-2 gap-10 items-start">
          <div>
            <div className="br-eyebrow">Common Austin Roofing Concerns</div>
            <h2 className="br-headline text-3xl sm:text-4xl mt-3">Hail. Wind. Heat. Storms. UV.</h2>
            <p className="mt-3 text-gray-700">Austin and Central Texas weather is hard on roofs. Sudden storms, hail events, intense summer heat, and aging shingles create predictable failure points — and unpredictable ones. Black Ridge inspects each roof in person, documents problem areas with photos, and explains what we find before quoting work.</p>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              { i: CloudRain, t: "Storm Damage", b: "Hail bruising, wind-lifted shingles, ridge cap damage." },
              { i: Sun, t: "Heat & UV", b: "Granule loss, dried sealants, brittle accessories." },
              { i: Wind, t: "High Winds", b: "Missing shingles, lifted edges, flashing failure." },
              { i: Droplets, t: "Water Intrusion", b: "Boot failures, flashing leaks, gutter-related issues." },
              { i: Hammer, t: "Aging Materials", b: "Roofs nearing end of useful life." },
              { i: ShieldCheck, t: "Honest Triage", b: "Document, quote, no insurance guarantees." },
            ].map(({ i: Icon, t, b }) => (
              <div key={t} className="p-4 bg-white border border-gray-200 rounded">
                <div className="w-9 h-9 bg-br-paper-2 rounded flex items-center justify-center mb-2"><Icon size={16} className="text-br-bronze" /></div>
                <div className="font-display tracking-widest uppercase text-sm">{t}</div>
                <div className="text-xs text-gray-600 mt-1">{b}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-pad bg-white">
        <div className="container-br">
          <div className="max-w-2xl mb-8">
            <div className="br-eyebrow">Our Process</div>
            <h2 className="br-headline text-3xl sm:text-4xl mt-3">From Inspection to Final Cleanup</h2>
          </div>
          <ProcessSteps />
        </div>
      </section>

      <section className="section-pad bg-br-paper">
        <div className="container-br grid lg:grid-cols-2 gap-10">
          <div>
            <div className="br-eyebrow">Roofing FAQs</div>
            <h2 className="br-headline text-3xl sm:text-4xl mt-3">What Austin Homeowners Ask</h2>
            <p className="mt-3 text-gray-700">We answer questions honestly. We document, recommend, and respect that the choice is yours.</p>
            <div className="mt-5 flex flex-wrap gap-3">
              <Link to="/services/roof-repair" className="br-btn-dark">Roof Repair Details</Link>
              <Link to="/services/roof-replacement" className="br-btn-secondary" style={{color:"#0a0a0a", borderColor:"#0a0a0a"}}>Roof Replacement</Link>
            </div>
          </div>
          <FAQAccordion faqs={FAQS} />
        </div>
      </section>

      <CTASection title="Schedule a Roof Inspection" subtitle="We'll walk the roof, document what we see, and explain the options." primaryLabel="Schedule Inspection" />
    </>
  );
}
